import React, { useContext, useEffect, createElement, useRef } from 'react';
import gsap from 'gsap';

import { useMainNavigation } from '@hooks';
import { REGION_TYPE } from '@components/layouts/MainLayout/MainLayout';
import SidebarNavigation from '@components/navigations/SidebarNavigation/SidebarNavigation';
import MessageBanner from '@common/components/MessageBanner/MessageBanner';
import { ButtonHamburger } from './ButtonHamburger';
import { Logo, LOGO_THEME } from './Logo';
import { List } from './List';
import { clamp, lerp } from './helpers';
import { getTaxonomyLabels } from '@utils';
import { REGION_STATE, RegionsContext } from '@common/components/Region/Region';

const styles = require('./MainNavigation.module.scss');

const MainNavigation = () => {
  const { regions, activate } = useContext(RegionsContext) || [];
  const region = regions.find(({ id }) => id === REGION_TYPE.page) || {};
  const columns = useMainNavigation();
  const labels = getTaxonomyLabels('header');

  const container = useRef(null);
  const background = useRef(null);

  const tween = useRef({
    opacity: 0,
    blur: false,
  });

  const getProgress = limit => {
    const { scrollPosition } = region;
    return clamp(scrollPosition, 0, limit) / limit;
  };

  const handleTick = () => {
    const progress = getProgress(500);

    // Set opacity
    tween.current.opacity = lerp(tween.current.opacity, progress, 0.15);
    background.current.style.opacity = tween.current.opacity;

    if (!tween.current.blur && progress === 1) {
      tween.current.blur = true;
      container.current.style.backdropFilter = 'blur(10px)';
    }

    if (tween.current.blur && progress < 1) {
      tween.current.blur = false;
      container.current.style.backdropFilter = 'none';
    }
  };

  const handleScroll = () => {
    const el = container.current,
      cl = styles.fixed,
      mb = el.previousSibling,
      pos = mb ? mb.getBoundingClientRect().bottom < 0 : true,
      neg = mb ? mb.getBoundingClientRect().bottom > 0 : false,
      state = el.classList.contains(cl);
    if (pos && !state) {
      el.classList.add(cl);
    } else if (neg && state) {
      el.classList.remove(cl);
    }
  };

  const handleClickButtonHamburger = () => {
    activate({
      id: REGION_TYPE.overlay,
      element: createElement(SidebarNavigation, {}),
    });
  };

  const setupEventListeners = () => {
    gsap.ticker.add(handleTick);
  };

  const removeEventListeners = () => {
    gsap.ticker.remove(handleTick);
  };

  useEffect(() => {
    if (region.state === REGION_STATE.active) {
      setupEventListeners();
    }

    return () => {
      removeEventListeners();
    };
  }, [region]);
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <MessageBanner />
      <nav className={styles.container} ref={container}>
        <span className={styles.background} ref={background} />
        <List items={columns[0]} />
        <Logo
          theme={LOGO_THEME.light}
          className={styles.logo}
          alt={labels.logo}
        />
        <List
          items={columns[1]}
          renderToggle={() => (
            <ButtonHamburger
              onClick={handleClickButtonHamburger}
              ariaLabel={labels.hamburger}
            />
          )}
        />
      </nav>
    </>
  );
};

export default MainNavigation;
